// Tanstack Start server function exposing the current Club's settings to client
// code (the auto-clockout banner reads it via useQuery). Resolves the
// authenticated scope and reads ONLY that Club's row via the club_id-filtered
// `getClubSettingsByClubId` — no `withTenant` is opened because `club_settings`
// is a GRANT-only, non-RLS table (ADR 0010); the WHERE club_id filter in the
// read is what isolates the Club.
//
// **File naming:** `.fn.ts` (not `.server.ts`) because client code imports the
// callable — Tanstack Start's RPC bridge handles the boundary. Coverage-excluded
// per the `*.fn.ts` glob in vitest.config.ts; the read logic it delegates to is
// covered by read-club-settings.integration.ts.
//
// Returns null when there is no active session (defensive — the _authenticated
// guard already redirects unauthenticated requests to /login).

import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

import type { ClubSettings } from './schema';

/** Stable query key for the current Club's settings. */
export function getClubSettingsQueryKey(
	clubId: string,
): readonly [string, string] {
	return ['club-settings', clubId] as const;
}

export const getClubSettingsServerFn = createServerFn({
	method: 'GET',
}).handler(async (): Promise<ClubSettings | null> => {
	const { getServerAuthService } = await import(
		'../authentication/composition.server'
	);
	const { getDb } = await import('../../_shared/db/composition');
	const { getClubSettingsByClubId } = await import('./read-club-settings');
	const { getServerObservability } = await import(
		'../../_shared/observability/composition.server'
	);

	const auth = getServerAuthService();
	const scope = await auth.resolveTenantScope(getRequest());
	if (!scope) return null;

	try {
		return await getClubSettingsByClubId(getDb(), scope.clubId);
	} catch (err) {
		const observability = await getServerObservability();
		observability.log({
			level: 'error',
			message: 'club.settings_fetch_failed',
			attrs: { clubId: scope.clubId },
		});
		throw err;
	}
});
